import {
  buildProfileRecipientRules,
  buildSelectedCountLabel,
  parseDelimitedQueryValues,
  partnerRecipientRules,
} from "./EmailAssistantManualQueryContextHelpers";
import type { EmailAssistantQueryContext } from "./EmailAssistantQueryContextTypes";

export const buildPartnerProfileEmailAssistantQueryContext = (
  searchParams: URLSearchParams,
): EmailAssistantQueryContext | null => {
  const partnerId = searchParams.get("eaPartnerId")?.trim();
  const organisationId = searchParams.get("eaOrganisationId")?.trim();
  if (!partnerId) {
    return null;
  }
  const selectedUserIds = parseDelimitedQueryValues(
    searchParams.get("eaSelectedUserIds"),
  );
  const defaultRule =
    selectedUserIds.length > 0 ? "selected_users" : "lead_user";

  return {
    contextSections: [
      {
        title: "Partner context",
        subtitle: "Partner recipient rules",
        emptyText: "No partner context was provided.",
        items: [
          {
            label: "Selected partner",
            detail:
              selectedUserIds.length > 0
                ? "One partner and selected partner users are in context. Email & Comms will resolve only those selected users unless the rule changes."
                : "One partner is in context. Choose lead partner user or all partner users before review.",
          },
          ...(selectedUserIds.length > 0
            ? [
                {
                  label: "Selected users",
                  detail: `${selectedUserIds.length} selected partner ${
                    selectedUserIds.length === 1 ? "user" : "users"
                  } from the partner profile.`,
                },
              ]
            : []),
          {
            label: "Recipient choices",
            detail:
              selectedUserIds.length > 0
                ? "Selected users, lead partner user, and all partner users are available for this profile handoff."
                : "Lead partner user and all partner users are available. Select users from the partner profile to open a selected-user handoff.",
          },
          {
            label: "Permission warnings",
            detail:
              "Email & Comms must resolve partner-user permissions before customer delivery can run.",
          },
        ],
      },
    ],
    manualJobContext: {
      organisationIds: organisationId ? [organisationId] : [],
      partnerIds: [partnerId],
      personIds: [],
      recipientRule: defaultRule,
      sourceKind: "partner_profile",
      userIds: selectedUserIds,
    },
    recipients:
      selectedUserIds.length > 0
        ? selectedUserIds.map((_, index) => ({
            name: `Selected partner user ${index + 1}`,
            email: "",
            sourceLabel: "Partner profile",
          }))
        : [
            {
              name: "Partner recipients",
              email: "",
              sourceLabel: "Partner profile",
            },
          ],
    recipientRuleOptions: buildProfileRecipientRules(
      partnerRecipientRules,
      selectedUserIds,
    ),
    recipientRuleValue: defaultRule,
    sourceContext: {
      sourceName: "Partner profile",
      sourceType: "CRM",
      contextLabel:
        "Opened from a CRM partner. Email & Comms owns partner-recipient resolution, permission warnings, preferences, Activity, audit, and any manual job.",
    },
  };
};

export const buildPartnersDashboardEmailAssistantQueryContext = (
  searchParams: URLSearchParams,
  selectedCount: string | null,
): EmailAssistantQueryContext | null => {
  const partnerIds = parseDelimitedQueryValues(
    searchParams.get("eaPartnerIds"),
  );
  const organisationIds = parseDelimitedQueryValues(
    searchParams.get("eaOrganisationIds"),
  );
  if (partnerIds.length === 0) {
    return null;
  }

  return {
    contextSections: [
      {
        title: "Selected partners",
        subtitle: buildSelectedCountLabel(
          partnerIds,
          selectedCount,
          "partner",
          "partners",
        ),
        emptyText: "No selected partners were provided.",
        items: [
          {
            label: "Recipient rule",
            detail:
              "Choose lead partner users or all partner users. Email & Comms resolves permissions before customer delivery.",
          },
          {
            label: "Open alerts",
            detail:
              "Review partner-related alerts before approving the manual email job.",
          },
        ],
      },
    ],
    manualJobContext: {
      organisationIds,
      partnerIds,
      personIds: [],
      recipientRule: "lead_user",
      sourceKind: "partners_dashboard",
      userIds: [],
    },
    recipients: partnerIds.map((_, index) => ({
      name: `Selected partner ${index + 1}`,
      email: "",
      sourceLabel: "Partners dashboard",
    })),
    recipientRuleOptions: partnerRecipientRules,
    recipientRuleValue: "lead_user",
    sourceContext: {
      sourceName: "Partners dashboard",
      sourceType: "CRM",
      contextLabel:
        "Opened from selected CRM partners. Email & Comms resolves partner recipients, permission warnings, preferences, and manual job safety.",
    },
  };
};
